import { useState, useEffect } from 'react';
import axios from 'axios';
import './AuditHippoUsers.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';

const API_URL = '/rest/sfmc/users/list';

const AVATAR_COLORS = ['indigo','amber','rose','sky','violet','lime','red','slate'];

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getInitials(name) {
    if (!name) return '??';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function normalizeUser(item, index) {
    const name = item.name || item.userName || 'Unknown User';
    const mfaEnabled = item.mfaEnabled === 1 || item.mfaEnabled === true;
    const apiEnabled = item.apiEnabled === 1 || item.apiEnabled === true;
    const status = item.status || 'Active';
    return {
        id:          item.id || item.sfmcUserId || `user-${index}`,
        name,
        initials:    getInitials(name),
        avatarColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
        email:       item.email || '—',
        username:    item.username || item.sfmcUserId || '—',
        role:        item.role || item.userRole || 'User',
        instance:    item.instance || 'Production',
        status,
        online:      status === 'Active',
        mfa:         mfaEnabled,
        api:         apiEnabled,
        permissions: item.permissions || '—',
        bus:         item.businessUnits || item.bus || 1,
        lastLogin:   formatDate(item.lastLogin || item.updatedAt),
        created:     formatDate(item.createdAt),
        createdBy:   item.createdBy || 'System',
        modules:     item.modules || [],
        _raw:        item,
    };
}

/* ─── Static Fallback Data ─── */
const USERS = [
    { id:'su-1', name:'Arjun Mehta', initials:'AM', avatarColor:'indigo', email:'arjun@acme.com', role:'Administrator', instance:'Production', status:'Active', online:true, mfa:true, api:true, permissions:'20/20', bus:4, lastLogin:'Today',
      modules:[{icon:'✉',label:'Email Studio',val:'✓',level:'full'},{icon:'⚙',label:'Automations',val:'✓',level:'full'},{icon:'⬡',label:'Data Extensions',val:'✓',level:'full'},{icon:'◈',label:'Journey Builder',val:'✓',level:'full'},{icon:'▤',label:'Cloud Pages',val:'✓',level:'full'},{icon:'👤',label:'Contacts',val:'✓',level:'full'},{icon:'🔑',label:'Admin',val:'2/3',level:'partial'}]},
    { id:'su-2', name:'Priya Sharma', initials:'PS', avatarColor:'amber', email:'priya@acme.com', role:'Marketing Cloud Administrator', instance:'Production', status:'Active', online:false, mfa:true, api:true, permissions:'18/20', bus:2, lastLogin:'Yesterday',
      modules:[{icon:'✉',label:'Email Studio',val:'✓',level:'full'},{icon:'⚙',label:'Automations',val:'✓',level:'full'},{icon:'⬡',label:'Data Extensions',val:'✓',level:'full'},{icon:'◈',label:'Journey Builder',val:'✓',level:'full'},{icon:'▤',label:'Cloud Pages',val:'✓',level:'full'},{icon:'👤',label:'Contacts',val:'✓',level:'full'},{icon:'🔑',label:'Admin',val:'1/3',level:'partial'}]},
    { id:'su-3', name:'Jake Thompson', initials:'JT', avatarColor:'rose', email:'jake@acme.com', role:'Content Creator', instance:'Production', status:'Active', online:false, mfa:false, api:false, permissions:'9/20', bus:1, lastLogin:'Mar 6',
      modules:[{icon:'✉',label:'Email Studio',val:'2/3',level:'none'},{icon:'⚙',label:'Automations',val:'1/3',level:'none'},{icon:'⬡',label:'Data Extensions',val:'1/3',level:'none'},{icon:'◈',label:'Journey Builder',val:'2/3',level:'partial'},{icon:'▤',label:'Cloud Pages',val:'2/3',level:'partial'},{icon:'👤',label:'Contacts',val:'1/2',level:'partial'},{icon:'🔑',label:'Admin',val:'—',level:'none'}]},
    { id:'su-4', name:'Lisa Chen', initials:'LC', avatarColor:'sky', email:'lisa@acme.com', role:'Analyst', instance:'Production', status:'Active', online:true, mfa:true, api:true, permissions:'6/20', bus:2, lastLogin:'Today',
      modules:[{icon:'✉',label:'Email Studio',val:'1/3',level:'none'},{icon:'⚙',label:'Automations',val:'1/3',level:'none'},{icon:'⬡',label:'Data Extensions',val:'1/3',level:'none'},{icon:'◈',label:'Journey Builder',val:'1/3',level:'none'},{icon:'▤',label:'Cloud Pages',val:'1/3',level:'none'},{icon:'👤',label:'Contacts',val:'1/2',level:'partial'},{icon:'🔑',label:'Admin',val:'—',level:'none'}]},
    { id:'su-5', name:'Marcus Webb', initials:'MW', avatarColor:'violet', email:'marcus@acme.com', role:'Automation Specialist', instance:'Production', status:'Active', online:false, mfa:true, api:true, permissions:'11/20', bus:3, lastLogin:'Mar 7',
      modules:[{icon:'✉',label:'Email Studio',val:'2/3',level:'partial'},{icon:'⚙',label:'Automations',val:'✓',level:'full'},{icon:'⬡',label:'Data Extensions',val:'✓',level:'full'},{icon:'◈',label:'Journey Builder',val:'1/3',level:'none'},{icon:'▤',label:'Cloud Pages',val:'1/3',level:'none'},{icon:'👤',label:'Contacts',val:'1/2',level:'partial'},{icon:'🔑',label:'Admin',val:'—',level:'none'}]},
    { id:'su-6', name:'Nina Patel', initials:'NP', avatarColor:'lime', email:'nina@acme.com', role:'Journey Builder Specialist', instance:'Production', status:'Active', online:true, mfa:true, api:false, permissions:'10/20', bus:1, lastLogin:'Today',
      modules:[{icon:'✉',label:'Email Studio',val:'✓',level:'full'},{icon:'⚙',label:'Automations',val:'1/3',level:'none'},{icon:'⬡',label:'Data Extensions',val:'1/3',level:'none'},{icon:'◈',label:'Journey Builder',val:'✓',level:'full'},{icon:'▤',label:'Cloud Pages',val:'1/3',level:'none'},{icon:'👤',label:'Contacts',val:'1/2',level:'partial'},{icon:'🔑',label:'Admin',val:'—',level:'none'}]},
    { id:'su-7', name:'Tom Bradley', initials:'TB', avatarColor:'red', email:'tom@acme.com', role:'Content Creator', instance:'Production', status:'Locked', online:false, mfa:false, api:false, permissions:'8/20', bus:1, lastLogin:'Feb 14',
      modules:[{icon:'✉',label:'Email Studio',val:'2/3',level:'none'},{icon:'⚙',label:'Automations',val:'1/3',level:'none'},{icon:'⬡',label:'Data Extensions',val:'1/3',level:'none'},{icon:'◈',label:'Journey Builder',val:'2/3',level:'partial'},{icon:'▤',label:'Cloud Pages',val:'2/3',level:'partial'},{icon:'👤',label:'Contacts',val:'—',level:'none'},{icon:'🔑',label:'Admin',val:'—',level:'none'}]},
    { id:'su-8', name:'Sarah Kim', initials:'SK', avatarColor:'slate', email:'sarah@acme.com', role:'Viewer', instance:'Sandbox', status:'Active', online:false, mfa:false, api:false, permissions:'6/20', bus:1, lastLogin:'Mar 4',
      modules:[{icon:'✉',label:'Email Studio',val:'1/3',level:'none'},{icon:'⚙',label:'Automations',val:'1/3',level:'none'},{icon:'⬡',label:'Data Extensions',val:'1/3',level:'none'},{icon:'◈',label:'Journey Builder',val:'1/3',level:'none'},{icon:'▤',label:'Cloud Pages',val:'1/3',level:'none'},{icon:'👤',label:'Contacts',val:'1/2',level:'partial'},{icon:'🔑',label:'Admin',val:'—',level:'none'}]},
];

const ROLES = ['All roles', 'Administrator', 'Marketing Cloud Administrator', 'Content Creator', 'Analyst', 'Automation Specialist', 'Journey Builder Specialist', 'Viewer'];

/* ─── Helpers ─── */
function getRoleBadgeColor(role) {
    switch (role) {
        case 'Administrator':                return '#6366F1';
        case 'Marketing Cloud Administrator': return '#0EA5E9';
        case 'Content Creator':              return '#F43F5E';
        case 'Analyst':                      return '#0EA5E9';
        case 'Automation Specialist':        return '#8B5CF6';
        case 'Journey Builder Specialist':   return '#10B981';
        case 'Viewer':                       return '#64748B';
        default:                             return '#94A3B8';
    }
}

/* ─── User Detail Panel (slideout) ─── */
function SUDetailPanel({ user, onClose }) {
    const [activeTab, setActiveTab] = useState('overview');
    if (!user) return null;
    const roleBg = getRoleBadgeColor(user.role);
    const permParts = (user.permissions || '0/0').split('/');
    const PERM_LABELS = { '✉': ['view','create','send'], '⚙': ['view','create','execute'], '⬡': ['view','create','write'], '◈': ['view','create','activate'], '▤': ['view','create','publish'], '👤': ['view','edit'], '🔑': ['roles','users','settings'] };

    return (
        <>
            <div className="step-panel-backdrop show" onClick={onClose} />
            <div className="step-panel show">
                {/* Header */}
                <div style={{ padding: '22px 26px 18px', borderBottom: '1px solid #F1F5F9', background: '#fff' }}>
                    <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className={`su-modal-avatar su-avatar-${user.avatarColor}`}>{user.initials}</div>
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                    <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: roleBg + '18', color: roleBg, border: `1px solid ${roleBg}40` }}>{user.role}</span>
                                    <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#ECFDF5', color: '#059669', border: '1px solid #A7F3D0' }}>● {user.status}</span>
                                    {user.online && <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#ECFDF5', color: '#059669', border: '1px solid #A7F3D0' }}>● Online</span>}
                                    {user.api && <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: '#EEF2FF', color: '#6366F1', border: '1px solid #C7D2FE' }}>API</span>}
                                </div>
                                <h2 style={{ fontWeight: 800, fontSize: 22, color: '#0F172A', margin: 0 }}>{user.name}</h2>
                                <p style={{ fontSize: 12, color: '#94A3B8', margin: '2px 0 0', fontFamily: 'monospace' }}>{user.email}</p>
                            </div>
                        </div>
                        <button className="step-panel-close" onClick={onClose}>✕</button>
                    </div>
                    {/* Quick stats row — matches HTML: ROLE, PERMISSIONS, LAST LOGIN */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 12, borderTop: '1px solid #F1F5F9', paddingTop: 14 }}>
                        <div>
                            <p style={{ fontSize: 9, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>Role</p>
                            <p style={{ fontWeight: 700, fontSize: 14, color: '#0F172A', margin: 0 }}>{user.role}</p>
                        </div>
                        <div>
                            <p style={{ fontSize: 9, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>Permissions</p>
                            <p style={{ fontWeight: 800, fontSize: 15, color: '#6366F1', margin: 0 }}>{user.permissions}</p>
                        </div>
                        <div>
                            <p style={{ fontSize: 9, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>Last Login</p>
                            <p style={{ fontWeight: 700, fontSize: 14, color: '#0F172A', margin: 0 }}>{user.lastLogin}</p>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="step-panel-tabs d-flex">
                    <button className={`modal-tab${activeTab === 'overview' ? ' active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                    <button className={`modal-tab${activeTab === 'permissions' ? ' active' : ''}`} onClick={() => setActiveTab('permissions')}>Permissions</button>
                    <button className={`modal-tab${activeTab === 'bu' ? ' active' : ''}`} onClick={() => setActiveTab('bu')}>BU Access</button>
                </div>

                {/* Tab Content */}
                <div style={{ padding: '20px 26px' }}>
                    {/* Overview Tab — matches HTML: Account Details table */}
                    {activeTab === 'overview' && (
                        <>
                            <p style={{ fontSize: 12, fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Account Details</p>
                            <div style={{ border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
                                {[
                                    { label: 'Email', value: user.email, mono: true },
                                    { label: 'Username', value: user.username || '—', mono: true },
                                    { label: 'Instance', value: user.instance, bold: true },
                                    { label: 'MFA', value: user.mfa ? '✓ Enabled' : 'Disabled', color: user.mfa ? '#059669' : '#DC2626' },
                                    { label: 'API Access', value: user.api ? '✓ Enabled' : 'Disabled', color: user.api ? '#059669' : '#DC2626' },
                                    { label: 'Created', value: user.created || '—' },
                                    { label: 'Created by', value: user.createdBy || 'System' },
                                ].map((r, i) => (
                                    <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F1F5F9' }}>
                                        <span style={{ fontSize: 13, color: '#64748B' }}>{r.label}</span>
                                        <span style={{
                                            fontSize: 13, fontWeight: 600, color: r.color || '#0F172A',
                                            ...(r.mono ? { fontFamily: "'Courier New', monospace" } : {}),
                                        }}>{r.value}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Permissions Tab */}
                    {activeTab === 'permissions' && (
                        <>
                            <p style={{ fontSize: 13, color: '#64748B', marginBottom: 16 }}>Granted permissions across all Marketing Cloud modules</p>
                            <div className="d-flex flex-column gap-3">
                                {(user.modules || []).map((m, i) => {
                                    const labels = PERM_LABELS[m.icon] || ['access'];
                                    const isFull = m.level === 'full';
                                    const granted = isFull ? labels.length : (m.val.includes('/') ? parseInt(m.val) : 0);
                                    return (
                                        <div key={i} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: '16px 18px' }}>
                                            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 10 }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span style={{ fontSize: 16 }}>{m.icon}</span>
                                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>{m.label}</span>
                                                </div>
                                                <span style={{
                                                    fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20, border: '1px solid',
                                                    ...(isFull
                                                        ? { background: '#ECFDF5', color: '#059669', borderColor: '#A7F3D0' }
                                                        : m.level === 'partial'
                                                            ? { background: '#FFFBEB', color: '#D97706', borderColor: '#FDE68A' }
                                                            : { background: '#F1F5F9', color: '#94A3B8', borderColor: '#E2E8F0' })
                                                }}>{isFull ? `${labels.length}/${labels.length} granted` : m.val}</span>
                                            </div>
                                            <div className="d-flex gap-2 flex-wrap">
                                                {labels.map((perm, j) => {
                                                    const isGranted = isFull || j < granted;
                                                    return (
                                                        <span key={perm} style={{
                                                            padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid',
                                                            ...(isGranted
                                                                ? { background: '#ECFDF5', color: '#059669', borderColor: '#A7F3D0' }
                                                                : { background: '#F8FAFC', color: '#CBD5E1', borderColor: '#E2E8F0' })
                                                        }}>{isGranted ? '✓ ' : ''}{perm}</span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* BU Access Tab */}
                    {activeTab === 'bu' && (
                        <>
                            <p style={{ fontSize: 13, color: '#64748B', marginBottom: 16 }}>Business unit access for {user.name}</p>
                            <div className="d-flex flex-column gap-2">
                                {Array.from({ length: user.bus || 1 }, (_, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #F1F5F9', borderRadius: 12, padding: '12px 16px' }}>
                                        <div style={{ width: 36, height: 36, borderRadius: 10, background: i === 0 ? '#EEF2FF' : '#F8FAFC', border: `1px solid ${i === 0 ? '#C7D2FE' : '#E2E8F0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: i === 0 ? '#6366F1' : '#94A3B8', flexShrink: 0 }}>
                                            {i === 0 ? '🏢' : '🏠'}
                                        </div>
                                        <div>
                                            <p style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', margin: 0 }}>{i === 0 ? 'ACME Corp — Production' : `Business Unit ${i + 1}`}</p>
                                            <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>{i === 0 ? 'Parent Business Unit' : 'Child Business Unit'}</p>
                                        </div>
                                        {i === 0 && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: '#6366F1', background: '#EEF2FF', border: '1px solid #C7D2FE', padding: '2px 8px', borderRadius: 10 }}>Default</span>}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

/* ─── User Row Card (matches HTML) ─── */
function SUUserCard({ user, onClick }) {
    const accentClass = user.status === 'Active' ? 'su-accent-active' : 'su-accent-locked';
    const roleBg = getRoleBadgeColor(user.role);

    return (
        <div className="su-user-card" onClick={onClick} style={{ cursor: 'pointer' }}>
            <div className={`su-accent ${accentClass}`} />
            <div className="su-user-card-content">
                <div className={`su-avatar su-avatar-${user.avatarColor}`}>{user.initials}</div>
                <div className="su-user-main">
                    <div className="su-user-name-row">
                        <div className="su-user-name">{user.name}</div>
                        {user.online && <span className="su-online-dot" title="Online" />}
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        <span className="su-badge su-badge-role" style={{ background: roleBg + '18', color: roleBg, border: `1px solid ${roleBg}40` }}>{user.role}</span>
                        <span className={`su-badge ${user.status === 'Active' ? 'su-status-active' : 'su-status-locked'}`}>
                            <span className={`su-badge-dot ${user.status === 'Active' ? 'su-dot-active' : 'su-dot-locked'}`} />{user.status}
                        </span>
                        {!user.mfa && <span className="su-badge su-badge-warn">⚠ No MFA</span>}
                    </div>
                </div>

                <div className="su-module-pills">
                    {(user.modules || []).map((m, i) => (
                        <span key={i} className={`su-pill su-pill-${m.level}`} title={`${m.label}: ${m.val}`}>
                            {m.icon} {m.val}
                        </span>
                    ))}
                </div>

                <div className="su-user-meta">
                    <div className="su-meta-item">
                        <div className="su-meta-label">Permissions</div>
                        <div className="su-meta-value su-meta-primary">{user.permissions}</div>
                    </div>
                    <div className="su-meta-item">
                        <div className="su-meta-label">BUs</div>
                        <div className="su-meta-value">{user.bus}</div>
                    </div>
                    <div className="su-meta-item">
                        <div className="su-meta-label">Last Login</div>
                        <div className="su-meta-value su-meta-muted">{user.lastLogin}</div>
                    </div>
                    <div className="su-flag-row">
                        {user.api && <span className="su-flag su-flag-api">API</span>}
                        {!user.mfa && <span className="su-flag su-flag-nomfa">No MFA</span>}
                        {user.instance === 'Sandbox' && <span className="su-flag su-flag-sandbox">Sandbox</span>}
                    </div>
                    <svg className="su-chevron" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5">
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </div>
            </div>
        </div>
    );
}

/* ─── Main Page ─── */
function AuditHippoUsers() {
    usePageTitle('AuditHippo Users');
    const [allUsers,       setAllUsers]       = useState([]);
    const [loading,        setLoading]        = useState(true);
    const [error,          setError]          = useState(null);
    const [searchQuery,    setSearchQuery]    = useState('');
    const [instanceFilter, setInstanceFilter] = useState('All');
    const [roleFilter,     setRoleFilter]     = useState('All roles');
    const [selectedUser,   setSelectedUser]   = useState(null);
    const [viewMode,       setViewMode]       = useState('list');

    useEffect(() => {
        axios.get(API_URL)
            .then(res => {
                const data = res.data;
                const list = Array.isArray(data) ? data : (data.items || data.users || data.data || []);
                setAllUsers(list.map(normalizeUser));
            })
            .catch(err => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    const USERS = allUsers;

    const filtered = USERS.filter(u => {
        if (instanceFilter !== 'All') {
            if (instanceFilter === 'Production' && u.instance !== 'Production') return false;
            if (instanceFilter === 'Sandbox' && u.instance !== 'Sandbox') return false;
        }
        if (roleFilter !== 'All roles' && u.role !== roleFilter) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (u.name && u.name.toLowerCase().includes(q))
                || (u.email && u.email.toLowerCase().includes(q))
                || (u.username && u.username.toLowerCase().includes(q));
        }
        return true;
    });

    // Dynamic roles from data
    const roleSet = new Set(USERS.map(u => u.role).filter(Boolean));
    const ROLES   = ['All roles', ...Array.from(roleSet)];

    // KPIs
    const totalUsers    = USERS.length;
    const activeCount   = USERS.filter(u => u.status === 'Active').length;
    const noMfaCount    = USERS.filter(u => !u.mfa).length;
    const apiCount      = USERS.filter(u => u.api).length;
    const lockedCount   = USERS.filter(u => u.status === 'Locked').length;

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header mb-4">
                    <h1>AuditHippo User Access</h1>
                    <p>All Marketing Cloud users &mdash; roles, permissions per module, and business unit access</p>
                </div>

                {loading && <div className="text-center py-5"><p>Loading users...</p></div>}
                {error && <div className="text-center py-5"><p className="text-danger">Failed to load: {error}</p></div>}

                {!loading && !error && <>
                {/* KPI Tiles */}
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon su-kpi-icon-users">👥</div>
                            <div>
                                <p className="kpi-label">Total Users</p>
                                <p className="kpi-value">{totalUsers}</p>
                                <p className="kpi-subtext">{activeCount} active</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon su-kpi-icon-mfa">⚠</div>
                            <div>
                                <p className="kpi-label">No MFA</p>
                                <p className="kpi-value">{noMfaCount}</p>
                                <p className="kpi-subtext">security risk</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon su-kpi-icon-api">⌥</div>
                            <div>
                                <p className="kpi-label">API Enabled</p>
                                <p className="kpi-value">{apiCount}</p>
                                <p className="kpi-subtext">programmatic access</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon su-kpi-icon-locked">🔒</div>
                            <div>
                                <p className="kpi-label">Locked</p>
                                <p className="kpi-value">{lockedCount}</p>
                                <p className="kpi-subtext">require attention</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="d-flex gap-3 mb-3 flex-wrap align-items-center">
                    <div className="search-wrapper flex-grow-1">
                        <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by name, email or username..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="filter-buttons su-filter-group">
                        {['All', 'Production', 'Sandbox'].map(s => (
                            <button
                                key={s}
                                className={`filter-btn${instanceFilter === s ? ' active' : ''}`}
                                onClick={() => setInstanceFilter(s)}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="su-role-select-wrapper">
                        <select
                            className="form-select su-role-select"
                            value={roleFilter}
                            onChange={e => setRoleFilter(e.target.value)}
                        >
                            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <div className="filter-buttons su-filter-group">
                        <button
                            className={`filter-btn${viewMode === 'list' ? ' active' : ''}`}
                            onClick={() => setViewMode('list')}
                        >
                            &#8801; List
                        </button>
                        <button
                            className={`filter-btn${viewMode === 'matrix' ? ' active' : ''}`}
                            onClick={() => setViewMode('matrix')}
                        >
                            &#8862; Matrix
                        </button>
                    </div>
                </div>

                {/* Results count */}
                <p className="su-results-count mb-3">
                    Showing <strong>{filtered.length}</strong> of {USERS.length} users
                </p>

                {/* User Cards */}
                {viewMode === 'list' ? (
                    filtered.length > 0 ? (
                        <div className="d-flex flex-column gap-3">
                            {filtered.map(u => (
                                <SUUserCard key={u.id} user={u} onClick={() => setSelectedUser(u)} />
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-state-icon">👤</div>
                            <h3 className="empty-state-title">No users found</h3>
                            <p className="empty-state-text">No users match your current filters.</p>
                        </div>
                    )
                ) : (
                    <div className="su-matrix-placeholder">
                        <div className="empty-state">
                            <div className="empty-state-icon">🔲</div>
                            <h3 className="empty-state-title">Matrix View</h3>
                            <p className="empty-state-text">Coming soon — a matrix view of users vs. permissions.</p>
                        </div>
                    </div>
                )}
                </>}
            </div>

            {selectedUser && <SUDetailPanel user={selectedUser} onClose={() => setSelectedUser(null)} />}
        </>
    );
}

export default AuditHippoUsers;
